import { type CSSProperties, type ReactElement, useEffect, useState } from 'react';
import { EFFECT_CLASS } from '@vello/tokens';

/**
 * Loading placeholders (V-LIB-FORMS, D508). The shimmer itself is the effect layer's `EFFECT_CLASS.skeleton` (it stops
 * under `prefers-reduced-motion`); these components give it shapes, and `useDelayedFlag` keeps a fast load from flashing
 * a skeleton at all.
 */

/** How long a load must last before its skeleton shows (ms). Under this, the screen goes straight to its content. */
export const SKELETON_DELAY_MS = 300;

/**
 * `true` once `active` has stayed true for `delayMs`; `false` the moment it turns false. With `delayMs <= 0` it follows
 * `active` immediately (first render included). A `loading` that flips off before the delay never reports `true`.
 */
export function useDelayedFlag(active: boolean, delayMs: number = SKELETON_DELAY_MS): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (!active) {
      setElapsed(false);
      return undefined;
    }
    if (delayMs <= 0) {
      setElapsed(true);
      return undefined;
    }
    const t = window.setTimeout(() => setElapsed(true), delayMs);
    return () => window.clearTimeout(t);
  }, [active, delayMs]);
  return active && (delayMs <= 0 || elapsed);
}

export interface SkeletonProps {
  /** `line` (one text line, default), `block` (a card / chart area) or `circle` (an avatar). */
  readonly variant?: 'line' | 'block' | 'circle';
  /** A CSS length — prefer a percentage or a `var(--space-*)` token. Defaults to the variant's own size. */
  readonly width?: string;
  readonly height?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

/** One shimmering placeholder shape. Decorative: it is `aria-hidden`; the region that owns it carries `aria-busy`. */
export function Skeleton({ variant = 'line', width, height, className, style }: SkeletonProps): ReactElement {
  const cls = `vello-skeleton vello-skeleton--${variant} ${EFFECT_CLASS.skeleton}${className !== undefined ? ` ${className}` : ''}`;
  return <span className={cls} aria-hidden="true" style={{ ...(width !== undefined ? { width } : {}), ...(height !== undefined ? { height } : {}), ...style }} />;
}

// Widths cycle so a stack of lines reads as text, not as a barcode.
const LINE_WIDTHS: readonly string[] = ['100%', '82%', '91%', '64%', '76%'];

/** The width of the `index`-th placeholder line (a fixed cycle — deterministic, so snapshots and layouts are stable). */
export function skeletonWidth(index: number): string {
  return LINE_WIDTHS[index % LINE_WIDTHS.length]!;
}

/**
 * A stack of placeholder lines for a panel or a text block that is still loading. `count` lines (default 3), the last
 * one shorter. Decorative — put `aria-busy` and a status label on the region that wraps it.
 */
export function SkeletonRows({ count = 3, className, style }: { count?: number; className?: string; style?: CSSProperties }): ReactElement {
  const n = Math.max(1, Math.floor(count));
  return (
    <div className={`vello-skeleton-rows${className !== undefined ? ` ${className}` : ''}`} aria-hidden="true" style={style}>
      {Array.from({ length: n }, (_, i) => (
        <Skeleton key={i} width={i === n - 1 && n > 1 ? '60%' : skeletonWidth(i)} />
      ))}
    </div>
  );
}
